import React, { ReactNode, useId } from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, SvgIcon, SxProps, Theme } from '@mui/material';

import { INDETERMINATE_TEXT, IndeterminateValue, isIndeterminate } from '../indeterminate';

interface Props<T> {
  readonly property: ReactNode;
  readonly value: T | IndeterminateValue | null;
  readonly options: ReadonlyArray<T>;
  readonly disabledOptions?: ReadonlyArray<T>;
  readonly getName?: (t: T) => React.ReactNode;
  readonly getValue?: (t: T) => string | number;
  readonly disabled?: boolean;
  readonly submit?: (value: T) => void;
  readonly statementColor?: string;
  readonly allowUnknownValues?: boolean;
  readonly unknownValueMessage?: string;
  readonly error?: boolean;
  readonly size?: 'small' | 'medium';

  /**
   * If not specified, will be true
   */
  readonly fullWidth?: boolean;
  readonly sx?: SxProps<Theme>;
  readonly margin?: 'dense' | 'normal' | 'none';
}

export function PropertySelectField<T extends {}>(props: Props<T>) {
  const getName = (source: T | IndeterminateValue) => {
    if (isIndeterminate(source)) return INDETERMINATE_TEXT;

    if (typeof props.getName === 'function') {
      return props.getName(source);
    }

    // TODO Figure out best way to ensure that the name can actually be rendered by React.
    return source as unknown as ReactNode;
  };

  const getValue = (source: T | null | IndeterminateValue) => {
    if (source === null) return '';
    if (isIndeterminate(source)) return INDETERMINATE_TEXT;

    if (typeof props.getValue === 'function') {
      return props.getValue(source);
    }

    return source.toString();
  };

  const { fullWidth, margin, options, sx, value, size, disabledOptions } = props;
  const unknownValue =
    value !== null && !isIndeterminate(value) && options.every((option) => getValue(option) !== getValue(value));

  const augmentedOptions: ReadonlyArray<T | IndeterminateValue> =
    value !== null && (unknownValue || isIndeterminate(value)) ? [value, ...options] : options;

  const onChange = (event: SelectChangeEvent<string | number>) => {
    const { value } = event.target;
    if (value === INDETERMINATE_TEXT || typeof props.submit !== 'function') return;

    props.submit(
      augmentedOptions.find((option) => (isIndeterminate(option) ? false : getValue(option) === value))! as T,
    );
  };

  const inputElementId = useId();
  const labelElementId = useId();

  const displayUnknownValueMessage = !props.allowUnknownValues && props.unknownValueMessage && unknownValue;

  const label = (
    <>
      {props.property}
      {displayUnknownValueMessage ? ` (${props.unknownValueMessage})` : ''}
    </>
  );

  return (
    <FormControl
      size={size ?? 'small'}
      sx={sx}
      fullWidth={fullWidth ?? true}
      margin={margin ?? 'normal'}
      error={props.error || (!props.allowUnknownValues && unknownValue)}
    >
      <InputLabel variant="outlined" id={labelElementId} htmlFor={inputElementId}>
        {label}
      </InputLabel>
      <Select
        size={size ?? 'small'}
        labelId={labelElementId}
        disabled={props.disabled}
        value={value === null ? '' : getValue(value)}
        fullWidth={fullWidth ?? true}
        onChange={onChange}
        inputProps={{ id: inputElementId }}
        IconComponent={props.disabled ? SvgIcon : undefined}
        label={label}
      >
        {augmentedOptions.map((opt) => (
          <MenuItem
            value={getValue(opt)}
            key={`${getValue(opt)}option`}
            disabled={isIndeterminate(opt) || disabledOptions?.includes(opt)}
          >
            {getName(opt)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
